import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Share2, Users, TrendingUp, Gift, Copy, Check, ExternalLink,
  Crown, ChevronRight, X, DollarSign, AlertCircle, ChevronUp, ChevronDown,
  Loader2, Shield, Clock, FileText
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useUser } from '../stores/user.js';
import { api } from '../lib/api.js';

/**
 * 推荐分享 + 三级分佣卡片
 *
 * 三级分佣:15% / 10% / 5%,最高 30%
 *
 * 工作模式:
 *   - 在线:Worker API(D1 数据)
 *   - 离线/dev:localStorage 模拟(立刻可用,演示效果)
 */

// 本地生成 6 位推荐码
function genCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let s = '';
  for (let i = 0; i < 6; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}

// 本地存储 key
const LS_KEY = 'fucai3d_referral_state';

// 从 localStorage 读本地推荐状态
function loadLocalState() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '{}');
  } catch { return {}; }
}

// 存到 localStorage
function saveLocalState(state) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch {}
}

// 从手机号派生稳定 ID
function phoneKey(phone) {
  return 'u_' + (phone || 'unknown').replace(/\D/g, '').slice(-8);
}

// 模拟三级分佣(根据本地注册的下级)
function getLocalStats(myPhone) {
  const state = loadLocalState();
  const myId = phoneKey(myPhone);
  const allUsers = Object.values(state.users || {});
  // 直推:被推荐码 XX 邀请的人(XX 是我的码)
  const myCode = state.codes?.[myId];
  const directs = allUsers.filter(u => u.referrer_code === myCode);
  // 二级:直推邀请的人
  const level2 = directs.flatMap(d => allUsers.filter(u => u.referrer_code === d.code));
  // 三级:二级邀请的人
  const level3 = level2.flatMap(l2 => allUsers.filter(u => u.referrer_code === l2.code));
  // 累计佣金(假设每个直推都开了 Pro 月付 169,按比例算)
  const lv1 = directs.length * 169 * 0.15;
  const lv2 = level2.length * 169 * 0.10;
  const lv3 = level3.length * 169 * 0.05;
  return {
    direct_count: directs.length,
    level2_count: level2.length,
    level3_count: level3.length,
    total_commission: +(lv1 + lv2 + lv3).toFixed(2),
    month_commission: +(lv1 + lv2 + lv3).toFixed(2),  // 简化为同样值
    directs: directs.map(d => ({ id: d.id, phone: d.phone, name: d.name, tier: d.tier, created_at: d.created_at })),
    level2: level2,
    level3: level3,
  };
}

// 模拟分佣记录
function getLocalCommissions(myPhone) {
  const stats = getLocalStats(myPhone);
  const records = [];
  let id = 1;
  // 直推 1 级
  for (const d of stats.directs) {
    records.push({
      id: id++, from_user_id: phoneKey(d.phone), from_phone: d.phone, from_name: d.name,
      amount: 25.35, percent: 15, level: 1, status: 'pending',
      created_at: d.created_at,
    });
  }
  // 二级
  for (const l2 of stats.level2) {
    records.push({
      id: id++, from_user_id: phoneKey(l2.phone), from_phone: l2.phone, from_name: l2.name,
      amount: 16.90, percent: 10, level: 2, status: 'pending',
      created_at: l2.created_at,
    });
  }
  // 三级
  for (const l3 of stats.level3) {
    records.push({
      id: id++, from_user_id: phoneKey(l3.phone), from_phone: l3.phone, from_name: l3.name,
      amount: 8.45, percent: 5, level: 3, status: 'pending',
      created_at: l3.created_at,
    });
  }
  return records;
}

export default function ReferralCard() {
  const { user } = useUser();
  const [mode, setMode] = useState('local');  // 'online' | 'local' | 'error'
  const [remoteData, setRemoteData] = useState(null);
  const [tab, setTab] = useState('overview');
  const [showRules, setShowRules] = useState(false);
  const [copied, setCopied] = useState(false);

  // 当前用户的推荐码(local 模式)
  const myPhone = user?.phone || '';
  const myId = phoneKey(myPhone);
  const localState = loadLocalState();
  if (user && !localState.codes?.[myId]) {
    // 首次生成推荐码
    if (!localState.codes) localState.codes = {};
    if (!localState.users) localState.users = {};
    localState.codes[myId] = genCode();
    // 当前用户注册到 local
    if (!localState.users[myId]) {
      localState.users[myId] = {
        id: myId, phone: myPhone, name: user.name,
        code: localState.codes[myId],
        tier: user.tier, referrer_code: null,
        created_at: Date.now(),
      };
    }
    saveLocalState(localState);
  }
  const myCode = localState.codes?.[myId] || '------';
  const shareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}${window.location.pathname}?ref=${myCode}`
    : `?ref=${myCode}`;

  // 尝试从后端拉数据(在线模式)
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await Promise.race([
          api.referrals.me(),
          new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 4000))
        ]);
        if (!cancelled && res?.ok) {
          setRemoteData(res);
          setMode('online');
        }
      } catch (e) {
        if (!cancelled) setMode('local');
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // 试用/过期用户:检查 user store 的 tier + trial_expires_at 判断
  function checkCanEarnCommission() {
    // 在线模式以服务器为准
    if (mode === 'online' && remoteData) {
      return {
        eligible: remoteData.can_earn_commission,
        reason: remoteData.reason,
      };
    }
    // 本地模式:从 user store 查
    const tier = user?.tier;
    const tierExpiresAt = user?.tier_expires_at;
    const trialExpiresAt = user?.trial_expires_at;
    const now = Date.now();
    // MAX 永久
    if (tier === 'MAX 买断' && (tierExpiresAt === null || tierExpiresAt === undefined)) {
      return { eligible: true, reason: 'MAX 买断用户,永久有效' };
    }
    // 付费中
    if (tierExpiresAt && tierExpiresAt > now) {
      return { eligible: true, reason: `${tier}有效期内` };
    }
    // 试用中
    if (trialExpiresAt && trialExpiresAt > now) {
      return { eligible: false, reason: '试用用户无分佣权,开通任意会员后可拿佣金' };
    }
    // 已过期
    return { eligible: false, reason: '档位已过期,续费后可拿佣金' };
  }
  const earnStatus = checkCanEarnCommission();

  // 实时刷 stats(本地)
  const [refreshTick, setRefreshTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setRefreshTick(x => x + 1), 5000);
    return () => clearInterval(t);
  }, []);

  const copyCode = () => {
    navigator.clipboard.writeText(myCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('推荐码已复制');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('推荐链接已复制');
  };

  const systemShare = async () => {
    const text = `用 蜜蜂空间,选号精准+大底池,4 层杀号思路真心稳。\n使用我的推荐码 ${myCode} 注册,立刻开始:\n${shareUrl}`;
    if (navigator.share) {
      try { await navigator.share({ title: '蜜蜂空间 推荐', text, url: shareUrl }); }
      catch (e) {}
    } else {
      navigator.clipboard.writeText(text);
      toast.success('推荐文案已复制');
    }
  };

  const fmtTime = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  if (!user) return null;

  // 选数据源
  let stats, directs, commissions, onlineShareUrl;
  if (mode === 'online' && remoteData) {
    stats = remoteData.stats;
    onlineShareUrl = remoteData.share_url;
    directs = [];  // tab 切换时按需加载
    commissions = [];
  } else {
    const localStats = getLocalStats(myPhone);
    stats = {
      direct_count: localStats.direct_count,
      total_commission: localStats.total_commission,
      month_commission: localStats.month_commission,
    };
    directs = localStats.directs;
    commissions = getLocalCommissions(myPhone);
  }

  return (
    <div className="space-y-3">
      {/* 试用/过期用户提示横幅(没有分佣权) */}
      {!earnStatus.eligible && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border-2 border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-orange-500/10 p-3"
        >
          <div className="flex items-start gap-2">
            <AlertCircle size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-bold text-amber-300 mb-1">⚠️ 暂无可拿佣金的权利</div>
              <div className="text-xs text-ink-1 leading-relaxed">{earnStatus.reason}</div>
              <div className="text-xs text-ink-2 mt-2">
                💡 你仍可以<strong className="text-brand-cyan">分享推荐码</strong>发展好友,
                一旦你<strong className="text-brand-gold">开通任意会员档位</strong>,
                未来 30 天内好友的充值将自动按 15%/10%/5% 给你分佣。
              </div>
              <button
                onClick={() => {
                  // 滚到顶部触发升级
                  document.getElementById('tier-cards')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="mt-2 text-xs font-semibold text-amber-300 underline"
              >
                立即开通会员 →
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* 主卡片 */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="card relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(168,85,247,0.15) 0%, rgba(34,211,238,0.1) 50%, rgba(251,191,36,0.08) 100%)',
        }}
      >
        <div className="absolute top-0 right-0 text-7xl opacity-5 select-none pointer-events-none">🎁</div>

        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Share2 size={18} className="text-brand-purple" />
              推荐分享 · 三级分佣
            </h2>
            <p className="text-xs text-ink-2 mt-1">邀请好友,一起拿佣金</p>
          </div>
          <button
            onClick={() => setShowRules(true)}
            className="btn-ghost text-xs px-2 py-1"
          >
            规则
          </button>
        </div>

        {/* 推荐码 */}
        <div className="rounded-xl border border-white/10 bg-white/5 p-3 mb-3">
          <div className="text-xs text-ink-2 mb-1 flex items-center justify-between">
            <span>我的推荐码</span>
            {mode === 'local' && (
              <span className="text-[10px] text-amber-400 flex items-center gap-0.5">
                <AlertCircle size={10} /> 本地模式
              </span>
            )}
            {mode === 'online' && (
              <span className="text-[10px] text-brand-cyan flex items-center gap-0.5">
                <Check size={10} /> 已同步后端
              </span>
            )}
          </div>
          <div className="flex items-center justify-between">
            <div className="font-mono text-2xl font-bold tracking-widest text-brand-gold">
              {myCode}
            </div>
            <button
              onClick={copyCode}
              className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? '已复制' : '复制'}
            </button>
          </div>
        </div>

        {/* 推荐链接 */}
        <div className="rounded-xl border border-white/10 bg-white/5 p-3 mb-3">
          <div className="text-xs text-ink-2 mb-1">推荐链接</div>
          <div className="flex items-center gap-2">
            <div className="flex-1 text-xs font-mono text-ink-1 truncate bg-black/20 rounded px-2 py-1.5">
              {shareUrl}
            </div>
            <button onClick={copyLink} className="btn-ghost text-xs px-2 py-1.5">
              <Copy size={12} />
            </button>
          </div>
        </div>

        {/* 分享按钮 */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <button
            onClick={systemShare}
            className="rounded-xl py-2.5 font-semibold text-sm flex items-center justify-center gap-2"
            style={{ background: 'linear-gradient(135deg, #22d3ee 0%, #a855f7 100%)', color: '#fff' }}
          >
            <Share2 size={16} />
            立即分享
          </button>
          <button
            onClick={systemShare}
            className="btn-secondary text-sm flex items-center justify-center gap-2"
          >
            <Gift size={14} />
            邀请好友
          </button>
        </div>

        {/* 统计 */}
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-lg bg-white/5 p-2 text-center">
            <div className="text-xs text-ink-2">直推人数</div>
            <div className="text-lg font-bold text-brand-cyan">{stats.direct_count}</div>
            <div className="text-[10px] text-ink-3">人</div>
          </div>
          <div className="rounded-lg bg-white/5 p-2 text-center">
            <div className="text-xs text-ink-2">本月佣金</div>
            <div className="text-lg font-bold text-brand-gold">¥{stats.month_commission}</div>
            <div className="text-[10px] text-ink-3">30 天</div>
          </div>
          <div className="rounded-lg bg-white/5 p-2 text-center">
            <div className="text-xs text-ink-2">累计佣金</div>
            <div className="text-lg font-bold text-brand-purple">¥{stats.total_commission}</div>
            <div className="text-[10px] text-ink-3">¥</div>
          </div>
        </div>
      </motion.div>

      {/* Tab 切换 */}
      <div className="card !p-2">
        <div className="grid grid-cols-5 gap-1">
          {[
            { key: 'overview', label: '规则' },
            { key: 'team', label: '团队' },
            { key: 'directs', label: `直推 ${stats.direct_count}` },
            { key: 'commissions', label: '分佣' },
            { key: 'withdraw', label: '💰提现' },
          ].map(t => (
            <button
              key={t.key}
              data-tab={t.key}
              onClick={() => setTab(t.key)}
              className={clsx(
                'rounded-lg py-2 text-xs font-semibold transition',
                tab === t.key
                  ? 'bg-brand-purple/20 text-brand-purple border border-brand-purple/30'
                  : 'text-ink-2 hover:bg-white/5'
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab 内容 */}
      <AnimatePresence mode="wait">
        {tab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="card"
          >
            <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
              <Crown size={14} className="text-brand-gold" />
              会员分佣奖励说明
            </h3>

            <div className="space-y-2 mb-3">
              <div className="flex items-center gap-2">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center font-bold text-sm flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #f97316 0%, #ef4444 100%)', color: '#fff' }}>
                  A<br/>(您)
                </div>
                <div className="flex-1 rounded-lg border border-brand-gold/30 bg-brand-gold/5 p-2 text-xs">
                  <div className="font-semibold text-brand-gold mb-0.5">A(您)邀请 B</div>
                  <div className="text-ink-2">B 开通 SVIP,A(您)可得分佣 <span className="font-bold text-brand-gold">15%</span></div>
                </div>
              </div>

              <div className="flex items-center gap-2 pl-3">
                <div className="w-14 h-11 rounded-lg flex items-center justify-center font-bold text-base flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', color: '#fff' }}>
                  B
                </div>
                <div className="flex-1 rounded-lg border border-brand-purple/20 bg-brand-purple/5 p-2 text-xs">
                  <div className="font-semibold mb-0.5">B 邀请 C</div>
                  <div className="text-ink-2">C 开通 SVIP,A(您)可得分佣 <span className="font-bold text-brand-purple">10%</span></div>
                </div>
              </div>

              <div className="flex items-center gap-2 pl-6">
                <div className="w-16 h-10 rounded-lg flex items-center justify-center font-bold text-lg flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)', color: '#fff' }}>
                  C
                </div>
                <div className="flex-1 rounded-lg border border-brand-cyan/20 bg-brand-cyan/5 p-2 text-xs">
                  <div className="font-semibold mb-0.5">C 邀请 D</div>
                  <div className="text-ink-2">D 开通 SVIP,A(您)可得分佣 <span className="font-bold text-brand-cyan">5%</span></div>
                </div>
              </div>

              <div className="flex items-center gap-2 pl-9 opacity-60">
                <div className="w-20 h-9 rounded-lg flex items-center justify-center font-bold flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)', color: '#fff' }}>
                  D
                </div>
                <div className="flex-1 rounded-lg border border-white/10 bg-white/5 p-2 text-xs">
                  <div className="font-semibold mb-0.5 text-ink-2">D 邀请 E</div>
                  <div className="text-ink-2">E 开通 SVIP,A(您)无分佣</div>
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 p-2.5 text-xs text-ink-2 leading-relaxed">
              <span className="text-amber-400 font-semibold">*注:</span> 用户 A 总计可享最高 <span className="font-bold text-brand-gold">30%</span> 分佣奖励,
              一旦成功建立绑定关系,该分佣权益将永久生效,长期畅享分佣。
            </div>

            {/* 本地模式:模拟邀请按钮 */}
            {mode === 'local' && (
              <div className="mt-3 rounded-lg border border-dashed border-brand-purple/30 bg-brand-purple/5 p-3">
                <div className="text-xs text-ink-2 mb-2">
                  <span className="text-brand-purple font-semibold">🎮 演示模式</span> · 当前未连接后端
                </div>
                <div className="text-xs text-ink-2 mb-2">
                  想看效果?点击下方按钮生成测试数据(模拟 B/C/D 注册并开通会员):
                </div>
                <button
                  onClick={() => {
                    // 生成模拟下级
                    const state = loadLocalState();
                    if (!state.users) state.users = {};
                    if (!state.codes) state.codes = {};
                    const myCodeVal = state.codes[myId];
                    const now = Date.now();
                    // 模拟 B(直推 1 级)
                    const bId = 'u_demo_b';
                    if (!state.users[bId]) {
                      state.users[bId] = {
                        id: bId, phone: '13900000001', name: '小王',
                        code: 'B' + genCode().slice(1),
                        tier: 'Pro 月付', referrer_code: myCodeVal,
                        created_at: now - 86400000,
                      };
                      state.codes[bId] = state.users[bId].code;
                    }
                    // 模拟 C(直推 2 级 - B 的直推)
                    const cId = 'u_demo_c';
                    if (!state.users[cId]) {
                      state.users[cId] = {
                        id: cId, phone: '13900000002', name: '小李',
                        code: 'C' + genCode().slice(1),
                        tier: 'Pro 月付', referrer_code: state.users[bId].code,
                        created_at: now - 3600000,
                      };
                      state.codes[cId] = state.users[cId].code;
                    }
                    // 模拟 D(直推 3 级 - C 的直推)
                    const dId = 'u_demo_d';
                    if (!state.users[dId]) {
                      state.users[dId] = {
                        id: dId, phone: '13900000003', name: '小张',
                        code: 'D' + genCode().slice(1),
                        tier: 'Pro 月付', referrer_code: state.users[cId].code,
                        created_at: now,
                      };
                      state.codes[dId] = state.users[dId].code;
                    }
                    saveLocalState(state);
                    setRefreshTick(x => x + 1);
                    toast.success('已生成 3 级测试数据,刷新生效');
                  }}
                  className="btn-primary text-xs w-full"
                >
                  🎲 生成 3 级测试数据(B→C→D)
                </button>
              </div>
            )}
          </motion.div>
        )}

        {tab === 'team' && (
          <TeamTab key="team" />
        )}

        {tab === 'withdraw' && (
          <WithdrawTab key="withdraw" onNeedAuth={() => toast.error('请先登录')} />
        )}

        {tab === 'directs' && (
          <motion.div
            key="directs"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="card"
          >
            <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
              <Users size={14} className="text-brand-cyan" />
              直推好友({stats.direct_count})
            </h3>
            {directs.length === 0 ? (
              <div className="text-center py-8 text-ink-3 text-sm">
                <Users size={32} className="mx-auto mb-2 opacity-30" />
                还没有直推好友
                <div className="text-xs mt-1">分享推荐码,让好友注册吧</div>
              </div>
            ) : (
              <div className="space-y-1.5">
                {directs.map(d => (
                  <div key={d.id} className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-cyan to-brand-purple flex items-center justify-center text-white text-xs font-bold">
                      {(d.name || d.phone || '?').slice(-2)}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold">{d.name || `用户${(d.phone || '').slice(-4)}`}</div>
                      <div className="text-xs text-ink-2">注册于 {fmtTime(d.created_at)}</div>
                    </div>
                    <span className={clsx(
                      'text-[10px] px-2 py-0.5 rounded-full',
                      d.tier === 'MAX 买断' ? 'bg-brand-gold/20 text-brand-gold' :
                      d.tier === 'Pro 年付' ? 'bg-brand-purple/20 text-brand-purple' :
                      'bg-brand-cyan/20 text-brand-cyan'
                    )}>
                      {d.tier}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {tab === 'commissions' && (
          <motion.div
            key="commissions"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="card"
          >
            <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
              <DollarSign size={14} className="text-brand-gold" />
              分佣记录(累计 ¥{stats.total_commission})
            </h3>
            {commissions.length === 0 ? (
              <div className="text-center py-8 text-ink-3 text-sm">
                <DollarSign size={32} className="mx-auto mb-2 opacity-30" />
                还没有分佣收入
                <div className="text-xs mt-1">好友开通会员,自动获得分佣</div>
              </div>
            ) : (
              <div className="space-y-1.5">
                {commissions.map(c => (
                  <div key={c.id} className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
                    <div className={clsx(
                      'w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold',
                      c.level === 1 ? 'bg-gradient-to-br from-brand-gold to-amber-500' :
                      c.level === 2 ? 'bg-gradient-to-br from-brand-purple to-pink-500' :
                      'bg-gradient-to-br from-brand-cyan to-blue-500'
                    )}>
                      L{c.level}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm">
                        <span className="font-semibold">{c.from_name || `用户${(c.from_phone || '').slice(-4)}`}</span>
                        <span className="text-xs text-ink-2 ml-1">开通 {c.percent}%</span>
                      </div>
                      <div className="text-xs text-ink-2">{fmtTime(c.created_at)} · L{c.level}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-brand-gold">+¥{c.amount}</div>
                      <div className="text-[10px] text-ink-3">{c.status}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* 规则弹窗 */}
      <AnimatePresence>
        {showRules && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4"
            onClick={() => setShowRules(false)}
          >
            <motion.div
              initial={{ y: 50 }}
              animate={{ y: 0 }}
              exit={{ y: 50 }}
              onClick={e => e.stopPropagation()}
              className="w-full md:max-w-md bg-bg-2 rounded-t-2xl md:rounded-2xl border border-white/10 p-5"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-bold">推荐分佣规则</h3>
                <button onClick={() => setShowRules(false)} className="btn-ghost p-1">
                  <X size={16} />
                </button>
              </div>
              <div className="space-y-2 text-sm text-ink-1">
                <div>① 推荐关系注册时绑定,一旦建立永久生效</div>
                <div>② 三级分佣比例:<span className="text-brand-gold font-bold">15% / 10% / 5%</span></div>
                <div>③ 最多拿 <span className="text-brand-gold font-bold">30%</span>,3 级外无分佣</div>
                <div>④ 下级开通任意付费档位(Pro 月付/年付/MAX)即触发分佣</div>
                <div>⑤ 退款时相应分佣冲销(status: pending → cancelled)</div>
                <div>⑥ 提现门槛 ¥100,可提现到微信/支付宝(联系客服)</div>
                <div>⑦ 同一用户多设备登录只算一个账号</div>
                <div>⑧ 恶意刷号/套利行为分佣清零</div>
              </div>
              <button onClick={() => setShowRules(false)} className="btn-primary w-full mt-4">
                我知道了
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── 我的团队 3 级树 ───
function TeamTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({ 1: true, 2: false, 3: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.referrals.team();
        if (!cancelled) setData(r);
      } catch (e) {
        if (!cancelled) toast.error('加载团队失败: ' + e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return <div className="card text-center py-6 text-xs text-ink-3">加载中...</div>;
  }
  if (!data) {
    return <div className="card text-center py-6 text-xs text-ink-3">暂无数据</div>;
  }

  const lv = data.levels;
  const lvlConfig = {
    1: { name: 'L1 直推', color: 'text-brand-cyan', border: 'border-brand-cyan/30', percent: '15%', label: '我拿 15%' },
    2: { name: 'L2 间推', color: 'text-brand-purple', border: 'border-brand-purple/30', percent: '10%', label: '我拿 10%' },
    3: { name: 'L3 三推', color: 'text-brand-gold', border: 'border-brand-gold/30', percent: '5%', label: '我拿 5%' },
  };

  return (
    <motion.div
      key="team"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="space-y-2"
    >
      {/* 总览卡 */}
      <div className="card !p-2.5 bg-gradient-to-r from-brand-cyan/5 to-brand-purple/5">
        <div className="flex items-center gap-2">
          <Users size={14} className="text-brand-cyan" />
          <div className="text-xs font-semibold">我的 3 级团队</div>
          <div className="flex-1" />
          <div className="text-[10px] text-ink-3">
            共 <span className="text-brand-cyan font-bold">{data.total.count}</span> 人 ·
            贡献 <span className="text-accent-success font-bold">¥{data.total.total_contribution.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* 三级分层 */}
      {[1, 2, 3].map(L => {
        const ldata = lv[L];
        const cfg = lvlConfig[L];
        const isExp = expanded[L];
        return (
          <div key={L} className={clsx('card !p-2.5 border', cfg.border)}>
            {/* 头部 */}
            <button
              onClick={() => setExpanded(e => ({ ...e, [L]: !e[L] }))}
              className="w-full flex items-center gap-2 text-left"
            >
              <div className={clsx(
                'w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm',
                L === 1 ? 'bg-brand-cyan/20 text-brand-cyan' :
                L === 2 ? 'bg-brand-purple/20 text-brand-purple' :
                'bg-brand-gold/20 text-brand-gold'
              )}>
                L{L}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold flex items-center gap-1.5">
                  {cfg.name}
                  <span className="text-[10px] text-ink-3">({cfg.label})</span>
                </div>
                <div className="text-[10px] text-ink-3 mt-0.5">
                  {ldata.count} 人 · 贡献 ¥{ldata.total_contribution.toFixed(2)}
                </div>
              </div>
              {isExp ? <ChevronUp size={14} className="text-ink-3" /> : <ChevronDown size={14} className="text-ink-3" />}
            </button>

            {/* 客户列表 */}
            {isExp && (
              <div className="mt-2 space-y-1.5">
                {ldata.users.length === 0 ? (
                  <div className="text-center text-[10px] text-ink-3 py-3">
                    还没有 L{L} 客户 · 多分享推广吧 💪
                  </div>
                ) : (
                  ldata.users.map(u => (
                    <div key={u.id} className="flex items-center gap-2 p-1.5 bg-bg-base/40 rounded">
                      <div className={clsx(
                        'w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold',
                        L === 1 ? 'bg-brand-cyan/20 text-brand-cyan' :
                        L === 2 ? 'bg-brand-purple/20 text-brand-purple' :
                        'bg-brand-gold/20 text-brand-gold'
                      )}>
                        {(u.name || u.phone || '?')[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-medium truncate">
                          {u.name || '匿名用户'}
                        </div>
                        <div className="text-[10px] text-ink-3 font-mono">
                          {u.phone?.slice(0, 3)}****{u.phone?.slice(-4)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={clsx(
                          'text-[9px] px-1.5 rounded',
                          u.tier?.includes('MAX') ? 'bg-brand-gold/20 text-brand-gold' :
                          u.tier?.includes('年付') ? 'bg-brand-purple/20 text-brand-purple' :
                          u.tier?.includes('月付') ? 'bg-brand-cyan/20 text-brand-cyan' :
                          'bg-white/10 text-ink-3'
                        )}>
                          {u.tier || '试用'}
                        </div>
                        <div className="text-[10px] text-accent-success font-mono mt-0.5">
                          +¥{(u.contribution || 0).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* 推广动力提示 */}
      <div className="card !p-2.5 bg-accent-success/5 border border-accent-success/20">
        <div className="text-[10px] text-ink-2 leading-relaxed">
          💡 <span className="font-semibold text-accent-success">推广动力</span>:
          你每推 1 个 <span className="text-brand-cyan">付费用户</span>,终身可拿 15% 佣金。
          你的 L1 用户推 1 个付费,你拿 10%(L2)。L1 → L2 → L3 形成的网,你最多拿 30% 累计佣金。
          拉更多 L1 = 更多佣金!
        </div>
      </div>
    </motion.div>
  );
}

// ============= 提现 Tab =============
function WithdrawTab({ onNeedAuth }) {
  const { user } = useUser();
  const [balance, setBalance] = useState({ available: 0, locked: 0, pending_release: 0, hold_days: 7 });
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRealName, setShowRealName] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [b, w] = await Promise.all([
        api.withdrawable(),
        api.withdrawals(),
      ]);
      if (b?.available !== undefined) setBalance(b);
      if (w?.withdrawals) setWithdrawals(w.withdrawals);
    } catch (e) {
      console.warn('加载提现数据失败:', e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const isPaid = user?.tier_expires_at === null
    || (user?.tier_expires_at && user.tier_expires_at > Date.now())
    || (user?.trial_expires_at && user.trial_expires_at > Date.now());  // 试用也算
  const isRealName = user?.id_verified;
  const hasPayMethod = user?.pay_method && user?.pay_account;

  if (loading) {
    return (
      <motion.div key="wd-loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="card text-center py-8">
        <Loader2 size={24} className="mx-auto animate-spin text-brand-cyan" />
        <div className="text-xs text-ink-3 mt-2">加载中...</div>
      </motion.div>
    );
  }

  return (
    <motion.div
      key="withdraw"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="space-y-3"
    >
      {/* 余额卡片 */}
      <div className="card relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(34,211,238,0.12) 0%, rgba(168,85,247,0.08) 100%)' }}>
        <div className="absolute top-0 right-0 text-7xl opacity-10 select-none pointer-events-none">💰</div>
        <div className="relative">
          <div className="text-[10px] text-ink-3 mb-1">可提现余额</div>
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold text-brand-cyan">¥{balance.available.toFixed(2)}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3 text-[10px]">
            <div className="bg-white/5 rounded p-1.5">
              <div className="text-ink-3">已锁定</div>
              <div className="text-accent-warn font-mono">¥{balance.locked.toFixed(2)}</div>
            </div>
            <div className="bg-white/5 rounded p-1.5">
              <div className="text-ink-3">{balance.hold_days}天内到账</div>
              <div className="text-ink-2 font-mono">¥{balance.pending_release.toFixed(2)}</div>
            </div>
          </div>
          <div className="text-[9px] text-ink-3 mt-2 flex items-center gap-1">
            <Clock size={9} /> 佣金到账需 {balance.hold_days} 天(防退款/洗钱)
          </div>
        </div>
      </div>

      {/* 提现前置条件 */}
      <div className="card !p-3">
        <div className="text-xs font-semibold mb-2">提现条件</div>
        <div className="space-y-1.5 text-[11px]">
          <CheckItem ok={isPaid} label="付费会员" hint="试用用户也可提现" />
          <CheckItem ok={isRealName} label="完成实名认证" hint={isRealName ? '已认证' : '身份证 + 收款方式'} />
          <CheckItem ok={hasPayMethod} label="已配置收款方式" hint="支付宝/微信/银行卡" />
          <CheckItem ok={balance.available >= 100} label="可提现余额 ≥ ¥100" />
        </div>
        {!isRealName && (
          <button
            onClick={() => setShowRealName(true)}
            className="mt-3 w-full py-2 rounded-lg bg-gradient-to-r from-brand-purple to-brand-cyan text-white text-xs font-semibold flex items-center justify-center gap-1.5"
          >
            <Shield size={12} /> 立即实名认证
          </button>
        )}
        {isRealName && balance.available >= 100 && (
          <button
            onClick={() => setShowWithdraw(true)}
            className="mt-3 w-full py-2 rounded-lg bg-gradient-to-r from-brand-cyan to-brand-purple text-white text-xs font-semibold flex items-center justify-center gap-1.5"
          >
            <DollarSign size={12} /> 申请提现
          </button>
        )}
        {isRealName && balance.available > 0 && balance.available < 100 && (
          <div className="mt-3 text-center text-[10px] text-ink-3">
            余额 ¥{balance.available.toFixed(2)} 不足 ¥100,继续推广赚佣金
          </div>
        )}
      </div>

      {/* 提现历史 */}
      <div className="card">
        <div className="text-xs font-semibold mb-2 flex items-center justify-between">
          <span>提现记录</span>
          <button onClick={load} className="text-[10px] text-brand-cyan flex items-center gap-1">
            <Loader2 size={9} className={loading ? 'animate-spin' : ''} /> 刷新
          </button>
        </div>
        {withdrawals.length === 0 ? (
          <div className="text-center py-6 text-ink-3 text-xs">
            <FileText size={20} className="mx-auto mb-1 opacity-30" />
            还没有提现记录
          </div>
        ) : (
          <div className="space-y-1.5">
            {withdrawals.map(w => (
              <WithdrawalRow key={w.id} w={w} />
            ))}
          </div>
        )}
      </div>

      {/* 弹窗 */}
      <AnimatePresence>
        {showRealName && (
          <RealNameModal
            onClose={() => setShowRealName(false)}
            onSuccess={() => {
              setShowRealName(false);
              load();
              toast.success('实名成功,现在可以提现');
            }}
          />
        )}
        {showWithdraw && (
          <WithdrawModal
            balance={balance}
            onClose={() => setShowWithdraw(false)}
            onSuccess={() => {
              setShowWithdraw(false);
              load();
              toast.success('提现申请已提交,等待审核');
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function CheckItem({ ok, label, hint }) {
  return (
    <div className="flex items-center gap-2">
      {ok ? (
        <div className="w-4 h-4 rounded-full bg-accent-success/20 text-accent-success flex items-center justify-center text-[10px]">✓</div>
      ) : (
        <div className="w-4 h-4 rounded-full bg-white/5 text-ink-3 flex items-center justify-center text-[10px]">○</div>
      )}
      <span className={ok ? 'text-ink-1' : 'text-ink-2'}>{label}</span>
      {hint && <span className="text-ink-3 text-[9px]">· {hint}</span>}
    </div>
  );
}

function WithdrawalRow({ w }) {
  const statusMap = {
    pending: { label: '待审核', color: 'text-accent-warn', bg: 'bg-accent-warn/10' },
    approved: { label: '已批准', color: 'text-brand-cyan', bg: 'bg-brand-cyan/10' },
    paid: { label: '已打款', color: 'text-accent-success', bg: 'bg-accent-success/10' },
    rejected: { label: '已拒绝', color: 'text-accent-danger', bg: 'bg-accent-danger/10' },
  };
  const s = statusMap[w.status] || statusMap.pending;
  return (
    <div className="p-2 rounded bg-white/5 border border-white/5">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-mono font-bold">¥{w.amount.toFixed(2)}</span>
        <span className={clsx('text-[10px] px-1.5 py-0.5 rounded', s.color, s.bg)}>{s.label}</span>
      </div>
      <div className="text-[9px] text-ink-3 flex items-center gap-2">
        <span>{w.pay_method === 'alipay' ? '支付宝' : w.pay_method === 'wechat' ? '微信' : '银行卡'}</span>
        <span>· {w.pay_account}</span>
        <span>· {new Date(w.created_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
      </div>
      {w.reject_reason && (
        <div className="text-[9px] text-accent-danger mt-1">原因:{w.reject_reason}</div>
      )}
    </div>
  );
}

// 实名认证 Modal
function RealNameModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({ real_name: '', id_card: '', pay_method: 'alipay', pay_account: '', pay_real_name: '' });
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const r = await api.realName(form);
      if (r?.ok) onSuccess();
      else toast.error(r?.error || '认证失败');
    } catch (e) {
      toast.error('认证失败: ' + e.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-3"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
        className="card max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="text-base font-bold flex items-center gap-2">
            <Shield size={16} className="text-brand-cyan" /> 实名认证
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/5"><X size={16} /></button>
        </div>
        <div className="text-[10px] text-ink-3 mb-3">
          ⚠ 提现前必须完成实名认证 + 配置收款方式。姓名/身份证号/收款人姓名三者必须一致。
        </div>
        <div className="space-y-2">
          <Field label="真实姓名" value={form.real_name} onChange={v => setForm(f => ({ ...f, real_name: v }))} placeholder="身份证姓名" />
          <Field label="身份证号" value={form.id_card} onChange={v => setForm(f => ({ ...f, id_card: v }))} placeholder="18 位身份证号" />
          <div className="border-t border-white/5 pt-2 mt-2">
            <div className="text-[10px] text-ink-3 mb-1.5">收款方式</div>
            <div className="grid grid-cols-3 gap-1.5 mb-2">
              {[['alipay', '支付宝', '💙'], ['wechat', '微信', '💚'], ['bank', '银行卡', '💳']].map(([v, l, e]) => (
                <button
                  key={v}
                  onClick={() => setForm(f => ({ ...f, pay_method: v }))}
                  className={clsx(
                    'p-2 rounded text-xs border transition',
                    form.pay_method === v
                      ? 'bg-brand-cyan/20 border-brand-cyan text-brand-cyan'
                      : 'bg-white/5 border-white/10 text-ink-2'
                  )}
                >
                  <div className="text-lg mb-0.5">{e}</div>
                  {l}
                </button>
              ))}
            </div>
            <Field label="收款账号" value={form.pay_account} onChange={v => setForm(f => ({ ...f, pay_account: v }))} placeholder={form.pay_method === 'bank' ? '银行卡号' : form.pay_method === 'alipay' ? '支付宝账号' : '微信号'} />
            <Field label="收款人姓名" value={form.pay_real_name} onChange={v => setForm(f => ({ ...f, pay_real_name: v }))} placeholder="必须与身份证姓名一致" />
          </div>
        </div>
        <button
          onClick={submit}
          disabled={loading}
          className="mt-3 w-full py-2 rounded-lg bg-gradient-to-r from-brand-purple to-brand-cyan text-white text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Shield size={14} />}
          {loading ? '认证中...' : '提交认证'}
        </button>
      </motion.div>
    </motion.div>
  );
}

// 提现 Modal
function WithdrawModal({ balance, onClose, onSuccess }) {
  const defaultAmt = Math.max(100, Math.min(100, balance.available));
  const [amount, setAmount] = useState(Math.min(balance.available, defaultAmt).toFixed(2));
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (loading) return;
    const amt = +amount;
    if (!amt || amt < 100) return toast.error('最低 ¥100');
    if (amt > 5000) return toast.error('单次最高 ¥5000');
    if (amt > balance.available) return toast.error('余额不足');
    setLoading(true);
    try {
      const r = await api.withdraw({ amount: amt });
      if (r?.ok) onSuccess();
      else toast.error(r?.error || '申请失败');
    } catch (e) {
      toast.error('申请失败: ' + e.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-3"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
        className="card max-w-md w-full"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="text-base font-bold flex items-center gap-2">
            <DollarSign size={16} className="text-brand-cyan" /> 申请提现
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/5"><X size={16} /></button>
        </div>
        <div className="text-[10px] text-ink-3 mb-3">
          💡 可提现 ¥{balance.available.toFixed(2)} · 锁定中 ¥{balance.locked.toFixed(2)} · 待到账 ¥{balance.pending_release.toFixed(2)} · 单次 ¥100 - ¥5000
        </div>
        <div className="space-y-2">
          <div>
            <div className="text-[10px] text-ink-3 mb-1">提现金额</div>
            <div className="flex items-center gap-2">
              <span className="text-xl text-ink-3">¥</span>
              <input
                type="number"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                step="0.01"
                min="100"
                max={Math.min(5000, balance.available)}
                className="flex-1 px-3 py-2 bg-bg-base border border-white/10 rounded-lg text-lg font-mono"
              />
            </div>
            <div className="flex gap-1 mt-1 flex-wrap">
              {[100, 200, 500, 1000, 5000, balance.available].filter((v, i, a) => v >= 100 && a.indexOf(v) === i).map(v => {
                const v2 = Math.min(v, balance.available);
                const disabled = v > balance.available;
                return (
                  <button
                    key={v}
                    onClick={() => !disabled && setAmount(v2.toFixed(2))}
                    disabled={disabled}
                    className={clsx(
                      'px-2 py-0.5 text-[10px] rounded border transition',
                      disabled
                        ? 'bg-white/5 border-white/5 text-ink-3 opacity-40 cursor-not-allowed'
                        : 'bg-white/5 border-white/10 text-ink-2 hover:border-brand-cyan/40'
                    )}
                  >
                    {v >= 5000 ? '全部' : `¥${v}`}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="p-2 rounded bg-bg-base/50 border border-white/5 text-[10px] text-ink-2">
            <div className="flex justify-between"><span>申请金额</span><span className="font-mono">¥{(+amount || 0).toFixed(2)}</span></div>
            <div className="flex justify-between"><span>手续费</span><span className="font-mono">¥0.00</span></div>
            <div className="flex justify-between font-semibold text-ink-1 mt-1"><span>实际到账</span><span className="font-mono">¥{(+amount || 0).toFixed(2)}</span></div>
          </div>
        </div>
        <div className="text-[10px] text-accent-warn mt-2">
          ⚠ 提现申请提交后需后台审核,1-3 个工作日内打款。
        </div>
        <button
          onClick={submit}
          disabled={loading}
          className="mt-3 w-full py-2 rounded-lg bg-gradient-to-r from-brand-cyan to-brand-purple text-white text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <DollarSign size={14} />}
          {loading ? '提交中...' : '确认申请'}
        </button>
      </motion.div>
    </motion.div>
  );
}

function Field({ label, value, onChange, placeholder }) {
  return (
    <div>
      <div className="text-[10px] text-ink-3 mb-1">{label}</div>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-2 py-1.5 bg-bg-base border border-white/10 rounded text-xs outline-none focus:border-brand-cyan/40"
      />
    </div>
  );
}
